import { ticketType } from "@agentcall/core/type"
import { useAtomValue } from "jotai"
import { useState } from "react"
import {
  BarChart2,
  Clipboard,
  Copy,
  CreditCard,
  Headphones,
  Home,
  LogOut,
  PieChart,
  Plus,
  Settings,
  Share2,
  Shield,
  User,
} from "react-feather"
import { toast } from "sonner"
import { authApi } from "@/api/client/auth"
import { ProfileListItem } from "@/components/profile/ProfileListItem"
import { QRCodeGenerator } from "@/components/qrcode"
import { Button } from "@/components/ui/button/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog/dialog"
import { Input } from "@/components/ui/input/input"
import { STRIPE_BILLING_URL } from "@/lib/config"
import { sessionUserAtom } from "@/store/session"

export function ProfilePage() {
  const sessionUser = useAtomValue(sessionUserAtom)
  const [isFormShareDialogOpen, setIsFormShareDialogOpen] = useState(false)

  const { mutateAsync: signOut, isPending } = authApi.useSignOut({
    onSuccess: () => {
      toast.success("サインアウトしました。")
      setTimeout(() => {
        window.location.href = "/signin"
      }, 1000)
    },
  })

  const handleLogout = () => {
    signOut()
  }

  const formUrl = `${window.location.origin}/new-tenant-register`

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(formUrl)
      toast.success("URLをコピーしました")
    } catch (error) {
      console.error("Failed to copy URL:", error)
      toast.error("URLのコピーに失敗しました")
    }
  }

  return (
    <div className="">
      <div className="py-5">
        <h1 className="text-xl font-bold text-[#354267]">マイページ</h1>
      </div>

      <div className="space-y-5">
        {/* ユーザー情報カード */}
        <Card>
          <CardContent className="p-4 rounded-[6px]">
            <div className="flex items-center gap-4">
              <div className="space-y-1">
                <h2 className="text-base font-semibold text-slate-600">
                  {sessionUser?.name || "未設定"}
                </h2>
                <div className="space-y-0.5">
                  <p className="text-xs font-semibold text-slate-500">{sessionUser?.email || ""}</p>
                  <p className="text-xs font-semibold text-slate-500">
                    オーナーID：{sessionUser?.id || ""}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* アカウントセクション */}
        <div className="space-y-2.5">
          <h2 className="text-base font-semibold text-[#354267] px-1">アカウント</h2>
          <Card>
            <ProfileListItem
              icon={<User className="w-[18px] h-[18px]" />}
              title="プロフィール設定"
              url="/profile/edit"
            />
          </Card>
        </div>

        {/* お支払い情報セクション */}
        <div className="space-y-2.5">
          <h2 className="text-base font-semibold text-[#354267] px-1">お支払い情報</h2>
          <Card className="divide-y divide-slate-200">
            <ProfileListItem
              icon={<BarChart2 className="w-[18px] h-[18px]" />}
              title="サービス利用状況"
              url="/profile/subscriptions"
            />
            <ProfileListItem
              icon={<PieChart className="w-[18px] h-[18px]" />}
              title="決済履歴"
              url={STRIPE_BILLING_URL}
              target="_blank"
            />
            <ProfileListItem
              icon={<CreditCard className="w-[18px] h-[18px]" />}
              title="お支払い方法"
              url={STRIPE_BILLING_URL}
              target="_blank"
            />
          </Card>
        </div>

        {/* サービス情報セクション */}
        <div className="space-y-2.5">
          <h2 className="text-base font-semibold text-[#354267] px-1">サービス情報</h2>
          <Card className="divide-y divide-slate-200">
            <ProfileListItem
              icon={<Headphones className="w-[18px] h-[18px]" />}
              title="サービスについてのお問合せ"
              url={`/tickets/add?category=${ticketType.TICKET_CATEGORY_SABISU_NI_TSUITE}`}
            />
            <ProfileListItem
              icon={<Plus className="w-[18px] h-[18px]" />}
              title="物件の新規追加"
              url="/properties/add"
            />
            <ProfileListItem
              icon={<Settings className="w-[18px] h-[18px]" />}
              title="運用ルール設定"
              url="/profile/operational-rule"
            />
            <ProfileListItem
              icon={<Share2 className="w-[18px] h-[18px]" />}
              title="プラスプラン 入居者情報フォーム共有"
              onClick={() => setIsFormShareDialogOpen(true)}
            />
          </Card>
        </div>

        {/* 運営情報セクション */}
        <div className="space-y-2.5">
          <h2 className="text-base font-semibold text-[#354267] px-1">運営情報</h2>
          <Card className="divide-y divide-slate-200">
            <ProfileListItem
              icon={<Shield className="w-[18px] h-[18px]" />}
              title="プライバシーポリシー"
              url="https://owners-age.com/privacy"
              target="_blank"
            />
            <ProfileListItem
              icon={<Clipboard className="w-[18px] h-[18px]" />}
              title="ご利用規約"
              disabled
            />
            <ProfileListItem
              icon={<Home className="w-[18px] h-[18px]" />}
              title="運営会社"
              url="https://owners-age.com/"
              target="_blank"
            />
          </Card>
        </div>

        {/* ログアウト */}
        <Card>
          <ProfileListItem
            icon={<LogOut className="w-[18px] h-[18px] text-red-500" />}
            title="ログアウト"
            isDestructive
            onClick={handleLogout}
            loading={isPending}
            disabled={isPending}
          />
        </Card>
      </div>

      {/* 入居者情報フォーム共有ダイアログ */}
      <Dialog open={isFormShareDialogOpen} onOpenChange={setIsFormShareDialogOpen}>
        <DialogContent className="max-w-[400px]">
          <DialogHeader className="space-y-2">
            <DialogTitle className="text-base font-semibold text-slate-900">
              入居者情報フォーム共有
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              URLとQRコードで共有できます
              <br />
              物件・部屋登録が完了してないと新規申し込みを受け付けることができません。
              <br />
              フォーム共有前に登録がお済であるかご確認ください。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 pt-2">
            {/* URL表示とコピーボタン */}
            <div className="space-y-2">
              <label htmlFor="form-url" className="text-sm font-semibold text-slate-600">
                フォームURL
              </label>
              <div className="flex gap-2">
                <Input id="form-url" value={formUrl} readOnly className="flex-1 text-sm" />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopyUrl}
                  aria-label="URLをコピー"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* QRコード表示 */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-600">QRコード</label>
              <div className="flex justify-center p-4 bg-slate-50 rounded-md">
                <QRCodeGenerator value={formUrl} size={256} level="M" />
              </div>
            </div>
          </div>

          {/* 閉じるボタン */}
          <div className="pt-4">
            <Button
              variant="outline"
              onClick={() => setIsFormShareDialogOpen(false)}
              className="w-full"
            >
              閉じる
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
